import { createContext, useContext, type ReactNode } from 'react';
import type { ProviderPayload } from '@/types/chat';
import type { Locale, ProductClickHandler } from '@/types/widget';
import type { FeedbackApi } from '@/api/feedback';

export interface ChatContextValue {
  payload: ProviderPayload;
  locale: Locale;
  onAddToCart: (variantId: string, quantity: number) => Promise<void> | void;
  onProductDetailsClick?: ProductClickHandler;
  productPageUrl?: string;
  /**
   * Triggered when the user clicks "Aperçu" on a product card. The chat
   * widget renders the focused product as a side panel.
   */
  onPreviewProduct?: (slug: string) => void;
  /**
   * Latest measured inline-size of the chat shell, in CSS px. Used by
   * downstream components to size themselves responsively against the
   * widget's actual width rather than the viewport. `null` until the
   * first measurement.
   */
  shellWidth?: number | null;
  /**
   * Feedback API client (thumbs up/down + reason submission). Null when
   * feedback isn't wired.
   */
  feedbackApi?: FeedbackApi | null;
  /**
   * Current backend session_id. Null before the first assistant response.
   * Feedback endpoints require this, so buttons hide themselves until set.
   */
  sessionId?: string | null;
  /**
   * Open the reason-collection dialog for the given backend message id.
   * Called by FeedbackButtons AFTER it has fired the "no-reason down" PUT.
   */
  openFeedbackDialog?: (messageBackendId: string) => void;
  /**
   * Serialize feedback writes for a single message id so a fast dialog
   * submit can't race with the still-in-flight "no-reason down" PUT.
   * Every writer (button toggle, dialog submit) enqueues via this.
   */
  enqueueFeedbackWrite?: <T>(messageId: string, fn: () => Promise<T>) => Promise<T>;
  /** 重试一条失败的消息。由 FailedBubble 的重试按钮触发。 */
  onRetryMessage?: (failedMessageId: string) => void;
  /**
   * True while any turn (send or retry) is in flight. FailedBubble uses
   * this to disable its retry button and show a "retrying…" label instead
   * of letting clicks no-op against useChatSession's `sending` guard —
   * under a rate limit, each ignored click still costs a probe + resend.
   */
  sending?: boolean;
  /**
   * The format (variant id) the reader picked, per product slug — shared by the
   * product cards and the preview pane so both always show the same one. Without
   * it (a card rendered outside ChatWidget) each card keeps its own choice.
   */
  chosenVariants?: Record<string, string>;
  chooseVariant?: (slug: string, variantId: string) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export interface ChatContextProviderProps extends ChatContextValue {
  children: ReactNode;
}

export function ChatContextProvider({ children, ...value }: ChatContextProviderProps): JSX.Element {
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

/**
 * Hook for section/chip components. Throws if used outside ChatContextProvider —
 * that's a programmer error worth surfacing loudly.
 */
export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChatContext must be used inside ChatContextProvider');
  }
  return ctx;
}
